import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';

import { GeoPosition, GeocodePrecision, roundPosition } from '../common/Geo';
import { GeoService } from './geo.service';

/** Where a {@link SearchLocation} came from — the step of the fallback chain that produced it. */
export type SearchLocationSource = 'device' | 'customer' | 'place';

/**
 * The searcher's chosen position, as the located search uses it. The coordinate is already
 * rounded to the 2 decimals it may leave the device with (backend #678 decision 9), whatever
 * the source delivered.
 */
export interface SearchLocation extends GeoPosition {
  /** What the header chip and the results bar show — „Mein Standort", „50667 Köln". */
  label: string;
  source: SearchLocationSource;
  /** The precision the backend resolved a place or the own address at; the device reports none. */
  precision?: GeocodePrecision | null;
}

/** The radius and the position the search runs with; `radiusKm` null is „egal" (no area). */
export interface SearchGeoState {
  location: SearchLocation | null;
  radiusKm: number | null;
}

/** Why the device position could not be used — the wording is the picker's. */
export type DeviceLocationFailure = 'denied' | 'unavailable' | 'unsupported';

export class DeviceLocationError extends Error {
  constructor(public readonly reason: DeviceLocationFailure) {
    super(`device location ${reason}`);
    this.name = 'DeviceLocationError';
  }
}

/** The radius chips above the results, in kilometres. „egal" is `null`. */
export const RADIUS_OPTIONS_KM: readonly number[] = [5, 10, 25, 50];

/** #172: the default on a first visit, decided 2026-10-21 (the mockup's selected chip). */
export const DEFAULT_RADIUS_KM = 25;

/** localStorage: the radius is a preference and may outlive the tab. */
export const RADIUS_STORAGE_KEY = 'search.radiusKm';
/** sessionStorage: the position is personal data and lives no longer than the tab (decision 9). */
export const LOCATION_STORAGE_KEY = 'search.location';

/** The stored marker for „the user removed the location" — distinct from „never chose one". */
const LOCATION_NONE = 'none';

/**
 * Backend #678 / web #172: the one owner of the searcher's position and radius, shared by the
 * header chip (`app-location-indicator`), the picker (`app-location-picker`) and the results page.
 *
 * The fallback chain the backend fixed — device position → own customer address → typed place →
 * no position — is implemented as three entry points the UI calls explicitly, because its first
 * rule is that **nothing asks the browser for a position on load**: a permission prompt without a
 * trigger gets dismissed, and a dismissed prompt is blocked for the origin from then on. So
 * {@link useDevicePosition} runs only on the user's click; {@link useCustomerAddress} is the one
 * step that may run unasked (it is the user's own registered address, no prompt involved), once
 * per page load and only while the user has neither chosen nor removed a location; and
 * {@link usePlace} is the manual entry for everyone else.
 *
 * A missing location never empties the list — the results page simply runs the plain search.
 */
@Injectable({ providedIn: 'root' })
export class SearchLocationService {
  private geoService = inject(GeoService);

  private readonly locationSubject = new BehaviorSubject<SearchLocation | null>(readStoredLocation());
  private readonly radiusSubject = new BehaviorSubject<number | null>(readStoredRadius());
  private customerLookupAttempted = false;

  /** The chosen position, or null. Replays the current value. */
  readonly location$: Observable<SearchLocation | null> = this.locationSubject.asObservable();

  /** The radius in km, or null for „egal". Replays the current value. */
  readonly radiusKm$: Observable<number | null> = this.radiusSubject.asObservable();

  /**
   * Position and radius together, one emission per real change of either — what the results
   * page re-runs its search on.
   */
  readonly state$: Observable<SearchGeoState> = combineLatest([this.location$, this.radiusKm$]).pipe(
    map(([location, radiusKm]) => ({ location, radiusKm })),
    distinctUntilChanged(sameState),
  );

  get location(): SearchLocation | null {
    return this.locationSubject.value;
  }

  get radiusKm(): number | null {
    return this.radiusSubject.value;
  }

  /**
   * True while this tab holds no decision at all — the user has neither chosen a location nor
   * removed one — which is the only state the unasked customer-address fallback may fill.
   */
  get isUndecided(): boolean {
    return readStorage(sessionStorage, LOCATION_STORAGE_KEY) === null;
  }

  setRadius(radiusKm: number | null): void {
    writeStorage(localStorage, RADIUS_STORAGE_KEY, radiusKm === null ? LOCATION_NONE : String(radiusKm));
    this.radiusSubject.next(radiusKm);
  }

  /**
   * Fallback step 1, on the user's explicit click only. Resolves to the rounded device position,
   * or errors with a {@link DeviceLocationError} naming why — the picker words it and offers the
   * manual entry; it must never leave the user with an empty list.
   */
  useDevicePosition(): Observable<SearchLocation> {
    return new Observable<SearchLocation>((subscriber) => {
      const geolocation = globalThis.navigator?.geolocation;
      if (!geolocation) {
        subscriber.error(new DeviceLocationError('unsupported'));
        return;
      }
      geolocation.getCurrentPosition(
        (position) => {
          const location: SearchLocation = {
            ...roundPosition({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
            label: $localize`:@@searchLocation.deviceLabel:Mein Standort`,
            source: 'device',
            precision: null,
          };
          this.set(location);
          subscriber.next(location);
          subscriber.complete();
        },
        (error) => {
          // PERMISSION_DENIED is 1; POSITION_UNAVAILABLE (2) and TIMEOUT (3) are both „not now".
          subscriber.error(new DeviceLocationError(error?.code === 1 ? 'denied' : 'unavailable'));
        },
        // Coarse is enough for a search on a kilometre grid, and it answers faster; a position
        // up to five minutes old is still the same neighbourhood.
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
      );
    });
  }

  /**
   * Fallback step 2: the own customer address, geocoded by the backend. Runs at most once per
   * page load and only while {@link isUndecided}; a 404 (no Resolved coordinate yet) or any other
   * failure resolves to null and is silent — this step is a convenience, not a feature the user
   * asked for, so nothing may surface for it. `label` is what the chip shows, e.g. „50667 Köln".
   */
  useCustomerAddress(label: string): Observable<SearchLocation | null> {
    if (this.customerLookupAttempted || !this.isUndecided) {
      return of(null);
    }
    this.customerLookupAttempted = true;
    return this.geoService.myLocation().pipe(
      map(
        (position): SearchLocation => ({
          ...roundPosition(position),
          label,
          source: 'customer',
          precision: position.geocodePrecision ?? null,
        }),
      ),
      // Re-checked at arrival: the user may have picked a location while the request ran, and
      // their choice wins over the fallback.
      tap((location) => {
        if (this.isUndecided) {
          this.set(location);
        }
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Fallback step 3: a typed postcode and/or city, through `GET api/geo/place`. Errors propagate
   * — the picker words the 404 („no such place") at the field and routes the rest through the
   * coded-error toast.
   */
  usePlace(postalCode: string | null | undefined, city: string | null | undefined): Observable<SearchLocation> {
    const label = [postalCode?.trim(), city?.trim()].filter(Boolean).join(' ');
    return this.geoService.place(postalCode, city).pipe(
      map(
        (place): SearchLocation => ({
          ...roundPosition(place),
          label,
          source: 'place',
          precision: place.precision ?? null,
        }),
      ),
      tap((location) => this.set(location)),
    );
  }

  /** „Standort entfernen": back to the plain search, and the fallback stays off for this tab. */
  clear(): void {
    writeStorage(sessionStorage, LOCATION_STORAGE_KEY, LOCATION_NONE);
    this.locationSubject.next(null);
  }

  private set(location: SearchLocation): void {
    writeStorage(sessionStorage, LOCATION_STORAGE_KEY, JSON.stringify(location));
    this.locationSubject.next(location);
  }
}

function sameState(a: SearchGeoState, b: SearchGeoState): boolean {
  return (
    a.radiusKm === b.radiusKm &&
    a.location?.latitude === b.location?.latitude &&
    a.location?.longitude === b.location?.longitude &&
    a.location?.label === b.location?.label
  );
}

function readStoredLocation(): SearchLocation | null {
  const raw = readStorage(sessionStorage, LOCATION_STORAGE_KEY);
  if (!raw || raw === LOCATION_NONE) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SearchLocation>;
    return typeof parsed?.latitude === 'number' && typeof parsed?.longitude === 'number' && typeof parsed?.label === 'string'
      ? (parsed as SearchLocation)
      : null;
  } catch {
    return null;
  }
}

function readStoredRadius(): number | null {
  const raw = readStorage(localStorage, RADIUS_STORAGE_KEY);
  if (raw === null) {
    return DEFAULT_RADIUS_KM;
  }
  if (raw === LOCATION_NONE) {
    return null;
  }
  const parsed = Number(raw);
  return RADIUS_OPTIONS_KM.includes(parsed) ? parsed : DEFAULT_RADIUS_KM;
}

/** Storage access is best-effort: a private window or blocked site data must not break the search. */
function readStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // ignored on purpose — see readStorage
  }
}
